<template>
  <div class="reporting-section" v-if="hasContent">
    <div class="section-header">
      <i class="material-icons section-icon">videocam</i>
      <h2>{{ $t('dash.reporting.section.videos.title') }}</h2>
    </div>

    <!-- KPIs page-level -->
    <div class="metric-row" v-if="kpis.length">
      <MetricCard
        v-for="k in kpis"
        :key="k.key"
        :value="k.value"
        :previous-value="k.prev || null"
        :label="k.label"
        :icon="k.icon"
      />
    </div>

    <!-- Graph sources de vues (total / organic / paid / unique) -->
    <template v-if="videoGraphSources.length && videoGraphLabels.length">
      <div class="graph-title">Sources de vues</div>
      <div class="graph-wrap">
        <Graph
          :id="`video-sources-${pageId}`"
          :data="videoGraphSources"
          :labels="videoGraphLabels"
          height="200px"
          width="100%"
        />
      </div>
    </template>

    <!-- Graph comportement (autoplay / manual / repeat / >30s) -->
    <template v-if="videoGraphBehavior.length && videoGraphLabels.length">
      <div class="graph-title">Comportement de lecture</div>
      <div class="graph-wrap">
        <Graph
          :id="`video-behavior-${pageId}`"
          :data="videoGraphBehavior"
          :labels="videoGraphLabels"
          height="200px"
          width="100%"
        />
      </div>
    </template>

    <!-- Liste vidéos -->
    <template v-if="videoPosts.length">
      <div class="video-table-title">{{ $t('dash.reporting.section.videos-list.title') }}</div>

      <div class="post-table">
        <div class="post-table__head" :style="{ gridTemplateColumns: gridTemplate }">
          <span class="col-header sortable" :class="sortClass('date')" @click="onSort('date')">
            {{ cap($t('dash.reporting.pdf.listItem.date')) }}<i class="material-icons sort-icon">{{ sortIcon('date') }}</i>
          </span>
          <span class="col-header">{{ cap($t('dash.reporting.pdf.listItem.publication')) }}</span>
          <span class="col-header sortable align-right" :class="sortClass('views')" @click="onSort('views')">
            {{ cap($t('dash.label.fb.video_views')) }}<i class="material-icons sort-icon">{{ sortIcon('views') }}</i>
          </span>
          <span v-if="network === 'facebook'" class="col-header sortable align-right" :class="sortClass('views15s')" @click="onSort('views15s')">
            Vues 15s<i class="material-icons sort-icon">{{ sortIcon('views15s') }}</i>
          </span>
          <span v-if="network === 'tiktok'" class="col-header align-right">Temps vu</span>
          <span v-if="network === 'tiktok'" class="col-header sortable align-right" :class="sortClass('finish')" @click="onSort('finish')">
            % complétion<i class="material-icons sort-icon">{{ sortIcon('finish') }}</i>
          </span>
          <span v-if="network === 'tiktok'" class="col-header sortable align-right" :class="sortClass('viewer')" @click="onSort('viewer')">
            Spectateurs<i class="material-icons sort-icon">{{ sortIcon('viewer') }}</i>
          </span>
          <span v-if="network === 'tiktok'" class="col-header align-right">Moy. durée</span>
          <span v-if="network === 'tiktok'" class="col-header sortable align-right" :class="sortClass('newFollower')" @click="onSort('newFollower')">
            Nvx abonnés<i class="material-icons sort-icon">{{ sortIcon('newFollower') }}</i>
          </span>
          <span v-if="network === 'instagram'" class="col-header sortable align-right" :class="sortClass('reach')" @click="onSort('reach')">
            {{ cap($t('dash.label.fb.impressions_reach')) }}<i class="material-icons sort-icon">{{ sortIcon('reach') }}</i>
          </span>
          <span class="col-header sortable align-right" :class="sortClass('reactions')" @click="onSort('reactions')">
            {{ cap($t('dash.reporting.reaction')) }}<i class="material-icons sort-icon">{{ sortIcon('reactions') }}</i>
          </span>
          <span class="col-header sortable align-right" :class="sortClass('comments')" @click="onSort('comments')">
            {{ cap($t('dash.reporting.comment')) }}<i class="material-icons sort-icon">{{ sortIcon('comments') }}</i>
          </span>
          <span v-if="network !== 'tiktok'" class="col-header sortable align-right" :class="sortClass('shares')" @click="onSort('shares')">
            {{ cap($t('dash.reporting.share')) }}<i class="material-icons sort-icon">{{ sortIcon('shares') }}</i>
          </span>
          <span class="col-header sortable align-right" :class="sortClass('engagement')" @click="onSort('engagement')">
            {{ cap($t('dash.reporting.pdf.listItem.engagement')) }}<i class="material-icons sort-icon">{{ sortIcon('engagement') }}</i>
          </span>
        </div>

        <div
          class="post-table__row"
          v-for="post in paginatedPosts"
          :key="post.id || post.post_id"
          :style="{ gridTemplateColumns: gridTemplate }"
        >
          <span class="cell-date">{{ fmtDate(post.created_time) }}</span>
          <div class="cell-post">
            <div class="post-thumb">
              <img v-if="post.attachment_url" :src="post.attachment_url" alt="">
              <i v-else class="material-icons">videocam</i>
            </div>
            <p class="post-msg">{{ post.message }}</p>
          </div>
          <span class="cell-num">{{ fmt(viewsFor(post)) }}</span>
          <span v-if="network === 'facebook'" class="cell-num">{{ fmt(post.video_view_count_15s || 0) }}</span>
          <span v-if="network === 'tiktok'" class="cell-num">{{ fmtTime(post.time_watched) }}</span>
          <span v-if="network === 'tiktok'" class="cell-num">{{ post.video_finish_rate != null ? post.video_finish_rate.toFixed(1) + '%' : '—' }}</span>
          <span v-if="network === 'tiktok'" class="cell-num">{{ post.viewer != null ? fmt(post.viewer) : '—' }}</span>
          <span v-if="network === 'tiktok'" class="cell-num">{{ fmtTime(post.video_per_duration) }}</span>
          <span v-if="network === 'tiktok'" class="cell-num">{{ post.video_new_follower != null ? fmt(post.video_new_follower) : '—' }}</span>
          <span v-if="network === 'instagram'" class="cell-num">{{ fmt(post.reach || 0) }}</span>
          <span class="cell-num">{{ fmt(reactionsFor(post)) }}</span>
          <span class="cell-num">{{ fmt(post.comments_count || 0) }}</span>
          <span v-if="network !== 'tiktok'" class="cell-num">{{ fmt(post.shares_count || 0) }}</span>
          <span class="cell-num">{{ post.reach_engagement ? post.reach_engagement.toFixed(2) + '%' : '—' }}</span>
        </div>
      </div>

      <div class="pagination" v-if="pageCount > 1">
        <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">
          <i class="material-icons">chevron_left</i>
        </button>
        <span class="page-info">{{ currentPage }} / {{ pageCount }}</span>
        <button class="page-btn" :disabled="currentPage === pageCount" @click="currentPage++">
          <i class="material-icons">chevron_right</i>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import MetricCard from './panels/MetricCard.vue'
import Graph from './Graph.vue'
import moment from 'moment'

const PAGE_SIZE = 20

function sumArr(arr) {
  if (!Array.isArray(arr) || !arr.length) return 0
  return arr.reduce(function(s, d) { return s + (d.total || 0) }, 0)
}
function sortByDate(arr) {
  return arr.slice().sort(function(a, b) { return a.date - b.date })
}

export default {
  name: 'ReportingVideos',
  components: { MetricCard, Graph },
  props: {
    stats: { type: Object, required: true },
    page: { type: Object, default: null },
  },
  data() {
    return {
      sortKey: 'date',
      sortDir: 'desc',
      currentPage: 1,
    }
  },
  computed: {
    network() { return this.page ? (this.page.network || '') : '' },
    pageId() { return this.page ? this.page.id : 'video' },
    videoPosts() {
      var posts = this.stats.posts || (this.stats.page && this.stats.page.posts) || []
      if (this.network === 'linkedin') return posts.filter(function(p) { return p.type === 'VIDEO' })
      return posts.filter(function(p) { return p.type === 'video' || p.type === 'VIDEO' })
    },
    hasPageVideoStats() {
      var p = this.stats.page
      return p && p.page_video_views && p.page_video_views.length > 0
    },
    hasContent() {
      return this.videoPosts.length > 0 || this.hasPageVideoStats
    },

    kpis() {
      var p = this.stats.page
      if (this.network === 'tiktok') {
        var totalViews = this.videoPosts.reduce(function(s, post) { return s + (post.view_count || 0) }, 0)
        return totalViews > 0 ? [{ key: 'views', value: totalViews, label: 'Vues', icon: 'videocam' }] : []
      }
      if (this.network !== 'facebook' || !p) return []
      var defs = [
        { key: 'views',    field: 'page_video_views',              label: 'Vues',                icon: 'videocam' },
        { key: 'paid',     field: 'page_video_views_paid',          label: 'Vues payantes',       icon: 'monetization_on' },
        { key: 'organic',  field: 'page_video_views_organic',       label: 'Vues organiques',     icon: 'eco' },
        { key: 'unique',   field: 'page_video_views_unique',        label: 'Vues uniques',        icon: 'person' },
        { key: 'autoplay', field: 'page_video_views_autoplayed',    label: 'Lecture automatique', icon: 'play_circle' },
        { key: 'manual',   field: 'page_video_views_click_to_play', label: 'Lecture manuelle',    icon: 'touch_app' },
        { key: 'repeat',   field: 'page_video_repeat_views',        label: 'Relecture',           icon: 'replay' },
        { key: 'complete', field: 'page_video_complete_views_30s',  label: '> 30 sec',            icon: 'done_all' },
      ]
      var list = []
      defs.forEach(function(d) {
        var arr = p[d.field]
        if (!Array.isArray(arr) || !arr.length) return
        var val = sumArr(arr)
        if (val > 0) list.push({ key: d.key, value: val, label: d.label, icon: d.icon })
      })
      return list
    },

    videoGraphLabels() {
      var p = this.stats.page
      if (!p) return []
      var base = p.page_video_views || p.page_video_views_organic || p.page_video_views_paid || []
      if (!base.length) return []
      return sortByDate(base).map(function(d) { return moment(d.date * 1000).format('MM/DD/YYYY') })
    },
    videoGraphSources() {
      var p = this.stats.page
      if (!p || this.network !== 'facebook') return []
      var datasets = []
      var total   = p.page_video_views || []
      var organic = p.page_video_views_organic || []
      var paid    = p.page_video_views_paid || []
      var unique  = p.page_video_views_unique || []
      if (total.length)   datasets.push({ label: 'Vues totales',    data: sortByDate(total).map(function(d)   { return d.total || 0 }), type: 'line' })
      if (organic.length) datasets.push({ label: 'Vues organiques', data: sortByDate(organic).map(function(d) { return d.total || 0 }), type: 'line', color: '#10B981' })
      if (paid.length)    datasets.push({ label: 'Vues payantes',   data: sortByDate(paid).map(function(d)    { return d.total || 0 }), type: 'line', color: '#F59E0B' })
      if (unique.length)  datasets.push({ label: 'Vues uniques',    data: sortByDate(unique).map(function(d)  { return d.total || 0 }), type: 'line', color: '#3B82F6' })
      return datasets
    },
    videoGraphBehavior() {
      var p = this.stats.page
      if (!p || this.network !== 'facebook') return []
      var datasets = []
      var auto     = p.page_video_views_autoplayed    || []
      var manual   = p.page_video_views_click_to_play || []
      var repeat   = p.page_video_repeat_views        || []
      var complete = p.page_video_complete_views_30s  || []
      if (auto.length)     datasets.push({ label: 'Lecture automatique', data: sortByDate(auto).map(function(d)     { return d.total || 0 }), type: 'line' })
      if (manual.length)   datasets.push({ label: 'Lecture manuelle',    data: sortByDate(manual).map(function(d)   { return d.total || 0 }), type: 'line', color: '#10B981' })
      if (repeat.length)   datasets.push({ label: 'Relecture',           data: sortByDate(repeat).map(function(d)   { return d.total || 0 }), type: 'line', color: '#F59E0B' })
      if (complete.length) datasets.push({ label: '> 30 sec',            data: sortByDate(complete).map(function(d) { return d.total || 0 }), type: 'line', color: '#EF4444' })
      return datasets
    },

    gridTemplate() {
      var cols = ['90px', '1fr', '70px']
      if (this.network === 'facebook')  cols.push('90px')
      if (this.network === 'tiktok')    cols.push('80px', '115px', '110px', '80px', '110px')
      if (this.network === 'instagram') cols.push('100px')
      cols.push('95px', '115px')
      if (this.network !== 'tiktok') cols.push('85px')
      cols.push('100px')
      return cols.join(' ')
    },

    sortedPosts() {
      var list = this.videoPosts.slice()
      var dir = this.sortDir === 'asc' ? 1 : -1
      var key = this.sortKey
      var self = this
      list.sort(function(a, b) {
        var va, vb
        if (key === 'date')       { va = new Date(a.created_time);  vb = new Date(b.created_time) }
        else if (key === 'views') { va = self.viewsFor(a); vb = self.viewsFor(b) }
        else if (key === 'views15s') { va = a.video_view_count_15s || 0; vb = b.video_view_count_15s || 0 }
        else if (key === 'finish')     { va = a.video_finish_rate || 0;    vb = b.video_finish_rate || 0 }
        else if (key === 'viewer')    { va = a.viewer || 0;               vb = b.viewer || 0 }
        else if (key === 'newFollower') { va = a.video_new_follower || 0; vb = b.video_new_follower || 0 }
        else if (key === 'reach')    { va = a.reach || 0;                vb = b.reach || 0 }
        else if (key === 'reactions'){ va = self.reactionsFor(a);        vb = self.reactionsFor(b) }
        else if (key === 'comments') { va = a.comments_count || 0;       vb = b.comments_count || 0 }
        else if (key === 'shares')   { va = a.shares_count || 0;         vb = b.shares_count || 0 }
        else if (key === 'engagement'){ va = a.reach_engagement || 0;    vb = b.reach_engagement || 0 }
        else                         { va = new Date(a.created_time);    vb = new Date(b.created_time) }
        if (va < vb) return -1 * dir
        if (va > vb) return 1 * dir
        return 0
      })
      return list
    },
    pageCount() { return Math.ceil(this.sortedPosts.length / PAGE_SIZE) },
    paginatedPosts() {
      var start = (this.currentPage - 1) * PAGE_SIZE
      return this.sortedPosts.slice(start, start + PAGE_SIZE)
    },
  },
  methods: {
    onSort(key) {
      if (this.sortKey === key) this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc'
      else { this.sortKey = key; this.sortDir = 'desc' }
      this.currentPage = 1
    },
    sortClass(key) { return { 'col-active': this.sortKey === key } },
    sortIcon(key) {
      if (this.sortKey !== key) return 'unfold_more'
      return this.sortDir === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
    },
    cap(str) { return str ? str.charAt(0).toUpperCase() + str.slice(1) : '' },
    viewsFor(post) {
      if (this.network === 'facebook')  return post.video_view_count || 0
      if (this.network === 'tiktok')    return post.post_impressions || post.view_count || 0
      return post.post_impressions || post.view_count || 0
    },
    reactionsFor(post) {
      if (post.post_reactions_sum != null) return post.post_reactions_sum
      return post.like_count || post.reactions_count || 0
    },
    fmt(n) {
      if (n == null) return '—'
      if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
      if (n >= 1000) return (n / 1000).toFixed(1) + 'k'
      return n.toString()
    },
    fmtTime(seconds) {
      if (!seconds) return '—'
      var s = Math.round(seconds)
      if (s < 60) return s + 's'
      var m = Math.floor(s / 60)
      if (m < 60) return m + 'min ' + (s % 60) + 's'
      return Math.floor(m / 60) + 'h ' + (m % 60) + 'min'
    },
    fmtDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' })
    },
  },
}
</script>

<style scoped>
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}
.section-icon { color: #6B1F4D; font-size: 22px; }
.section-header h2 { font-size: 16px; font-weight: 700; color: #121826; margin: 0; }

.metric-row { display: flex; flex-wrap: wrap; gap: 14px; margin-bottom: 20px; }

.graph-title {
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 0 0 6px;
}
.graph-wrap { width: 100%; margin-bottom: 20px; }

.video-table-title {
  font-size: 13px;
  font-weight: 600;
  color: #555E75;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1edf5;
}

.post-table { width: 100%; overflow-x: auto; }
.post-table__head,
.post-table__row {
  display: grid;
  gap: 8px;
  align-items: center;
  padding: 8px 4px;
}
.post-table__head { border-bottom: 1px solid #f1edf5; padding-bottom: 10px; }
.post-table__row { border-bottom: 1px solid #f9f7fb; font-size: 13px; color: #121826; }
.post-table__row:last-child { border-bottom: none; }
.align-right { text-align: right; justify-content: flex-end; }

.col-header {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  min-width: 0;
}
.col-header.sortable { cursor: pointer; user-select: none; }
.col-header.sortable:hover { color: #6B1F4D; }
.col-header.col-active { color: #6B1F4D; }
.sort-icon { font-size: 14px; opacity: 0.6; }
.col-active .sort-icon { opacity: 1; }

.cell-date { color: #9ba3b7; font-size: 12px; white-space: nowrap; min-width: 0; overflow: hidden; }
.cell-post { display: flex; align-items: flex-start; gap: 10px; min-width: 0; }
.post-thumb {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 5.6px;
  overflow: hidden;
  background: #f1edf5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0a8d0;
}
.post-thumb img { width: 100%; height: 100%; object-fit: cover; }
.post-thumb .material-icons { font-size: 18px; }
.post-msg {
  font-size: 12px;
  color: #555E75;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin: 0;
}
.cell-num { text-align: right; font-size: 13px; font-weight: 500; min-width: 0; }

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #f1edf5;
}
.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e0d0ea;
  background: #fff;
  color: #6B1F4D;
  cursor: pointer;
  transition: all 0.15s;
}
.page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.page-btn:not(:disabled):hover { background: #f1edf5; }
.page-btn .material-icons { font-size: 18px; }
.page-info { font-size: 13px; color: #555E75; font-weight: 500; min-width: 60px; text-align: center; }
</style>
